import { ReactNode, useEffect, useRef } from "react";

import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import {
    draggable,
    dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { cx } from "class-variance-authority";

import { ButtonsBehaviour, ElementsAndSectionsBehaviour } from "./behaviours";
import * as theme from "./theme.css";
import { ElementSpecId, ElementPosition, ITEM_TYPES } from "./types";

interface DraggedElement {
    type: "ELEMENT";
    id: ElementSpecId;
    position: ElementPosition;
}

interface ElementContainerProps<ElementButtonSpecId extends string> {
    id: ElementSpecId;
    position: ElementPosition;
    className?: string;
    buttonsBehaviour: ButtonsBehaviour<ElementButtonSpecId>;
    elementsAndSectionsBehaviour: ElementsAndSectionsBehaviour;
    children: ReactNode;
}
function ElementContainer<ElementButtonSpecId extends string>(
    props: ElementContainerProps<ElementButtonSpecId>
) {
    const {
        id,
        position,
        className: maybeClassName,
        buttonsBehaviour,
        elementsAndSectionsBehaviour,
        children,
    } = props;

    const enableDrag = elementsAndSectionsBehaviour.type !== "FIXED";
    const enableDrop = enableDrag || buttonsBehaviour.type !== "HIDDEN";

    const ref = useRef<HTMLDivElement>(null);

    // Mirror `position` so getInitialData / getData read the latest values
    // without re-subscribing on every render (the parent rebuilds `position`
    // each render).
    const positionRef = useRef(position);
    // eslint-disable-next-line react-hooks/refs
    positionRef.current = position;
    const idRef = useRef(id);
    // eslint-disable-next-line react-hooks/refs
    idRef.current = id;

    // Register the element as draggable and/or a drop target for buttons/elements.
    useEffect(() => {
        const el = ref.current;
        if (!el) {
            return;
        }
        const cleanups: Array<() => void> = [];
        if (enableDrag) {
            cleanups.push(
                draggable({
                    element: el,
                    getInitialData: (): Record<string, unknown> => {
                        const data: DraggedElement = {
                            type: ITEM_TYPES.ELEMENT,
                            id: idRef.current,
                            position: positionRef.current,
                        };
                        return { ...data };
                    },
                })
            );
        }
        if (enableDrop) {
            cleanups.push(
                dropTargetForElements({
                    element: el,
                    canDrop: ({ source }) => {
                        const t = source.data.type;
                        return (
                            t === ITEM_TYPES.BUTTON || t === ITEM_TYPES.ELEMENT
                        );
                    },
                    getData: () => ({
                        dropKind: "ELEMENT" as const,
                        sectionId: positionRef.current.sectionId,
                        column: positionRef.current.location.column,
                        row: positionRef.current.location.row,
                    }),
                })
            );
        }
        return combine(...cleanups);
    }, [enableDrag, enableDrop]);

    const className = maybeClassName
        ? maybeClassName
        : cx(theme.elementContainer, enableDrag && theme.draggable);

    return (
        <div ref={ref} className={className}>
            {children}
        </div>
    );
}

interface ElementPlaceholderContainerProps {
    children: ReactNode;
}
function ElementPlaceholderContainer(props: ElementPlaceholderContainerProps) {
    const { children } = props;
    return <div className={theme.elementPlaceholderContainer}>{children}</div>;
}

interface SectionPlaceholderElementContainerProps {
    children: ReactNode;
}
function SectionPlaceholderElementContainer(
    props: SectionPlaceholderElementContainerProps
) {
    const { children } = props;
    return (
        <div className={theme.sectionPlaceholderElementContainer}>
            {children}
        </div>
    );
}

export {
    type DraggedElement,
    ElementContainer,
    type ElementContainerProps,
    ElementPlaceholderContainer,
    type ElementPlaceholderContainerProps,
    SectionPlaceholderElementContainer,
    type SectionPlaceholderElementContainerProps,
};
